import { useEffect, useState } from "react";
import idx from "idx";
import useRouter from "found/useRouter";

import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Divider from "@material-ui/core/Divider";
import FormField from "@material-ui/core/TextField";
import FormButton from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import BrowserLink from "found/Link";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";

import graphql from "babel-plugin-relay/macro";
import { useFragment, useMutation } from "relay-hooks";

import Page from "common/Page";
import useForm from "common/hooks/useForm";
import useAuth from "common/hooks/useAuth";
import SnackBar, { openSnackbar } from "common/SnackBar";
import FormFeedback from "common/FormFeedback";
import CircularProgress from "common/FacebookProgress";
import logo from "common/images/hfa_logo.svg";

import { ResetPasswordUser_data$key } from "./__generated__/ResetPasswordUser_data.graphql";
import { ResetPasswordUserMutationResponse as ResetPasswordResponse } from "./__generated__/ResetPasswordUserMutation.graphql";

const mutation = graphql`
  mutation ResetPasswordUserMutation($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      id
    }
  }
`;

const fragmentSpec = graphql`
  fragment ResetPasswordUser_data on VerifyTokenPayload {
    user {
      id
      email
      firstName
      lastName
    }
    error
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    form: {
      // marginTop: theme.spacing(6),
    },
    card: {
      // padding: `${theme.spacing(4)}px ${theme.spacing(3)}px`,
      // [theme.breakpoints.up('md')]: {
      //   padding: `${theme.spacing(8)}px ${theme.spacing(6)}px`,
      // },
    },
    cardHeader: {
      padding: theme.spacing(1, 2),
      textAlign: "center",
    },
    cardTitle: {
      fontSize: "1.25rem",
    },
    feedback: {
      marginTop: theme.spacing(2),
    },
    title: {
      fontSize: 14,
    },
    error: {
      textTransform: "capitalize",
    },
    button: {
      // margin: theme.spacing(3, 0, 2),
      color: "white",
    },
    buttonWrapper: {
      position: "relative",
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -9,
      marginLeft: -12,
    },
    option: {
      fontSize: 15,
      "& > span": {
        marginRight: 10,
        fontSize: 18,
      },
    },
  })
);

// Create your own validationSchema schema property should be the same in
// validationSchema in-order a validation to works in your input.
const validatorSchema = {};

interface IProps {
  data?: ResetPasswordUser_data$key;
  error?: any | null;
}

export default function ResetPasswordUser(props: IProps) {
  const classes = useStyles();
  const { isLoggedin, getCurrentUser } = useAuth();
  const { router } = useRouter();
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const res = useFragment(fragmentSpec, props?.data!);

  const schema = {
    id: res?.user?.id!,
    newPassword: "",
    confirmPassword: "",
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    disable,
    values,
    errors,
    setError,
    // setValue,
    handleOnChange,
    handleOnSubmit,
  } = useForm(schema, validatorSchema, onSubmitForm);

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: ({ resetPassword }: ResetPasswordResponse) => {
      setIsResetSuccess(true);
    },
    onError: (error) => {
      setError("error", error);
      openSnackbar("Reset Password failed");
    },
  });

  useEffect(() => {
    if (isLoggedin) {
      const role = getCurrentUser()?.role!;
      if (role === "admin") {
        router.push("/admin/dashboard");
        // window.location.reload();
        return;
      }

      if (role !== "") {
        // throw new RedirectException(`/dashboard`);

        setTimeout(() => {
          // console.log("you can see me after 1 seconds");
          router.push("/");
        }, 500);
      }
    }
  }, [isLoggedin, router, getCurrentUser]);

  useEffect(() => {
    document.title = "Reset Password";
  }, []);

  // finally call the mutation to save to backend
  function onSubmitForm(data?: any) {
    // console.log('No errors, submit callback called!');
    mutate({
      variables: {
        input: {
          id: res?.user?.id!,
          newPassword: data?.newPassword!,
          confirmPassword: data?.confirmPassword!,
        },
      },
    });
  }

  if (!res) return null;

  if (res && res?.error!) {
    return (
      <Page className={classes.root} title="Reset Password">
        <Container maxWidth="xs">
          <Box p={1} m={1} justifyContent="center" textAlign="center">
            <img
              className={classes.root}
              src={logo}
              alt={process.env.REACT_APP_SITE_NAME}
            />
          </Box>

          <Card className={classes.card} raised>
            <CardHeader title="Invalid Request" />
            <Divider />
            <CardContent>
              <Typography component="div" variant="body2">
                {" "}
                {res?.error!}{" "}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Page>
    );
  }

  if (isResetSuccess) {
    return (
      <Page className={classes.root} title="Reset Password">
        <Container maxWidth="xs">
          <Box p={1} m={1} justifyContent="center" textAlign="center">
            <img
              className={classes.root}
              src={logo}
              alt={process.env.REACT_APP_SITE_NAME}
            />
          </Box>

          <Card className={classes.card} raised>
            <CardHeader title="Success!" />
            <Divider />
            <CardContent>
              <Typography component="div" variant="body2">
                Your account password has been successfully changed
              </Typography>
            </CardContent>
            <Divider />
            <CardActions>
              <Grid container>
                <Grid item xs>
                  <Link component={BrowserLink} to="/user/login">
                    Back to login
                  </Link>
                </Grid>
              </Grid>
            </CardActions>
          </Card>
        </Container>
      </Page>
    );
  }

  const errorCheck = (key: string) => {
    const message = idx(
      errors,
      (x: any) => x!.error!.extensions!.validation[key]
    );
    return message ? true : false;
  };

  const hasError = (inputName: string) => !!(errors && errors[inputName]);

  return (
    <Page className={classes.root} title="Forgot Password">
      <Container maxWidth="xs">
        <Box p={1} m={1} justifyContent="center" textAlign="center">
          <img
            className={classes.root}
            src={logo}
            alt={process.env.REACT_APP_SITE_NAME}
          />
        </Box>

        <Card className={classes.card}>
          <CardHeader title="Create Password" />
          <Divider />

          <SnackBar variant="error" />

          <form onSubmit={handleOnSubmit} className={classes.form}>
            <CardContent>
              <FormField
                name="newPassword"
                type="password"
                label="New Password"
                value={values!.newPassword}
                onChange={handleOnChange}
                error={errorCheck("NewPassword")}
                variant="outlined"
                margin="normal"
                required
                fullWidth
              />

              <FormField
                name="confirmPassword"
                type="password"
                label="Confirm Password"
                value={values!.confirmPassword}
                onChange={handleOnChange}
                error={errorCheck("ConfirmPassword")}
                variant="outlined"
                margin="normal"
                required
                fullWidth
              />

              {hasError("error") ? (
                <FormFeedback className={classes.feedback} error>
                  {idx(errors, (x: any) => x!.error!.message)}
                </FormFeedback>
              ) : null}
            </CardContent>

            <Divider />
            <CardActions>
              <div className={classes.buttonWrapper}>
                <FormButton
                  className={classes.button}
                  disabled={disable || loading}
                  type="submit"
                  // size="large"
                  color="primary"
                  variant="contained"
                  fullWidth
                >
                  Save Password
                </FormButton>
                {loading && (
                  <CircularProgress className={classes.buttonProgress} />
                )}
              </div>
            </CardActions>
          </form>
        </Card>
      </Container>
    </Page>
  );
}
